/**
 * Exact decimal arithmetic over canonical decimal strings.
 *
 * Money never travels as a float in this system: a rate read from a vendor page
 * is a string, is compared as a string, and is multiplied with integer maths, so
 * that re-serializing a catalog can never invent a price change out of binary
 * rounding. See design/schemas/README.md, "Rates and usage".
 *
 * Canonical form is nonnegative, without exponent, sign, leading integer zeros
 * or trailing fractional zeros: "0", "2", "0.000001".
 */

export interface Decimal {
  /** Unscaled integer value. */
  readonly units: bigint;
  /** Number of digits after the decimal point; always >= 0. */
  readonly scale: number;
}

export class DecimalFormatError extends Error {
  constructor(
    readonly value: string,
    reason: string,
  ) {
    super(`Invalid decimal ${JSON.stringify(value)}: ${reason}`);
    this.name = "DecimalFormatError";
  }
}

const LOOSE = /^(\d+)(?:\.(\d+))?$/;

/**
 * Parse a nonnegative decimal literal. Accepts the non-canonical spellings a
 * source may legitimately publish ("0.50", "007") but rejects signs, exponents,
 * empty fractions and non-finite words, which are data defects rather than
 * formatting differences.
 */
export function parseDecimal(value: string): Decimal {
  if (typeof value !== "string") throw new DecimalFormatError(String(value), "not a string");
  const trimmed = value.trim();
  if (trimmed === "") throw new DecimalFormatError(value, "empty");
  if (/[eE]/.test(trimmed)) throw new DecimalFormatError(value, "exponent notation is not accepted");
  if (/^[+-]/.test(trimmed)) throw new DecimalFormatError(value, "signed values are not accepted");
  const match = LOOSE.exec(trimmed);
  if (!match) throw new DecimalFormatError(value, "not a nonnegative decimal literal");
  const [, whole, fraction = ""] = match;
  return { units: BigInt(whole + fraction), scale: fraction.length };
}

/** True when the string is already in canonical form. */
export function isCanonicalDecimal(value: string): boolean {
  if (typeof value !== "string") return false;
  if (!LOOSE.test(value)) return false;
  return formatDecimal(parseDecimal(value)) === value;
}

/**
 * Place the decimal point in an unscaled integer. Rates are nonnegative, but
 * deltas are not, so the sign is separated before padding — otherwise a value
 * like -0.5 renders as "-.5".
 */
function place(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, "0");
  const body =
    scale === 0
      ? digits
      : `${digits.slice(0, digits.length - scale)}.${digits.slice(digits.length - scale)}`;
  return negative ? `-${body}` : body;
}

/** Render a decimal in canonical form (trailing fractional zeros removed). */
export function formatDecimal(decimal: Decimal): string {
  let { units, scale } = decimal;
  while (scale > 0 && units % 10n === 0n) {
    units /= 10n;
    scale -= 1;
  }
  return place(units, scale);
}

/** Render with exactly `places` fractional digits, rounding half up. */
export function formatFixed(decimal: Decimal, places: number): string {
  const rounded = roundHalfUp(decimal, places);
  return place(rounded.units, places);
}

/** Canonicalize a decimal literal string; throws on malformed input. */
export function canonicalizeDecimal(value: string): string {
  return formatDecimal(parseDecimal(value));
}

export function decimalFromInteger(value: bigint | number): Decimal {
  return { units: BigInt(value), scale: 0 };
}

export const DECIMAL_ZERO: Decimal = { units: 0n, scale: 0 };

function rescale(decimal: Decimal, scale: number): bigint {
  if (scale < decimal.scale) throw new Error("rescale must not lose precision");
  return decimal.units * 10n ** BigInt(scale - decimal.scale);
}

function commonScale(a: Decimal, b: Decimal): number {
  return Math.max(a.scale, b.scale);
}

export function addDecimal(a: Decimal, b: Decimal): Decimal {
  const scale = commonScale(a, b);
  return { units: rescale(a, scale) + rescale(b, scale), scale };
}

export function subtractDecimal(a: Decimal, b: Decimal): Decimal {
  const scale = commonScale(a, b);
  return { units: rescale(a, scale) - rescale(b, scale), scale };
}

export function multiplyDecimal(a: Decimal, b: Decimal): Decimal {
  return { units: a.units * b.units, scale: a.scale + b.scale };
}

/** Exact scaling by a nonnegative power of ten, used for per-million rates. */
export function shiftDecimal(decimal: Decimal, tenthPowers: number): Decimal {
  if (tenthPowers >= 0) return { units: decimal.units * 10n ** BigInt(tenthPowers), scale: decimal.scale };
  return { units: decimal.units, scale: decimal.scale - tenthPowers };
}

export function compareDecimal(a: Decimal, b: Decimal): number {
  const scale = commonScale(a, b);
  const left = rescale(a, scale);
  const right = rescale(b, scale);
  return left < right ? -1 : left > right ? 1 : 0;
}

export function isZero(decimal: Decimal): boolean {
  return decimal.units === 0n;
}

export function isNegative(decimal: Decimal): boolean {
  return decimal.units < 0n;
}

/** Round to `places` fractional digits, half away from zero ("half up"). */
export function roundHalfUp(decimal: Decimal, places: number): Decimal {
  if (decimal.scale <= places) {
    return { units: rescale(decimal, places), scale: places };
  }
  const divisor = 10n ** BigInt(decimal.scale - places);
  const negative = decimal.units < 0n;
  const magnitude = negative ? -decimal.units : decimal.units;
  const quotient = magnitude / divisor;
  const remainder = magnitude % divisor;
  const rounded = remainder * 2n >= divisor ? quotient + 1n : quotient;
  return { units: negative ? -rounded : rounded, scale: places };
}

/**
 * Division rounded half up at `places`. Percentage deltas are presentation
 * figures rather than money, so a terminating exact quotient is not required.
 */
export function divideDecimal(a: Decimal, b: Decimal, places: number): Decimal {
  if (b.units === 0n) throw new Error("division by zero");
  // a/b * 10^places = ua * 10^(sb - sa + places) / ub
  const shift = b.scale - a.scale + places;
  let numerator = a.units;
  let denominator = b.units;
  if (shift >= 0) numerator *= 10n ** BigInt(shift);
  else denominator *= 10n ** BigInt(-shift);
  const negative = numerator < 0n !== denominator < 0n;
  const absNumerator = numerator < 0n ? -numerator : numerator;
  const absDenominator = denominator < 0n ? -denominator : denominator;
  const quotient = absNumerator / absDenominator;
  const remainder = absNumerator % absDenominator;
  const rounded = remainder * 2n >= absDenominator ? quotient + 1n : quotient;
  return { units: negative ? -rounded : rounded, scale: places };
}

/**
 * A decimal from a JavaScript number, for consumers whose stored rates are
 * floats. `String(n)` is the shortest string that round-trips, so it recovers
 * the literal that was written in every case where that literal was itself
 * shortest. Exponent form is expanded exactly; negatives and non-finite values
 * are rejected, because a rate is neither.
 */
export function decimalFromNumber(value: number): Decimal {
  if (!Number.isFinite(value)) throw new DecimalFormatError(String(value), "not finite");
  if (value < 0) throw new DecimalFormatError(String(value), "signed values are not accepted");
  const text = String(value);
  const exponent = /^(\d+)(?:\.(\d+))?e([+-]\d+)$/i.exec(text);
  if (!exponent) return parseDecimal(text);
  const [, whole, fraction = ""] = exponent;
  const digits = parseDecimal(fraction === "" ? whole : `${whole}.${fraction}`);
  return shiftDecimal(digits, Number(exponent[3]));
}
